import {
    useEffect,
    useMemo,
    useState } from "react";
import { Link,
    useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import {
    adminDeleteInquiry,
    adminExportInquiries,
    adminListInquiries,
    adminMe,
    adminUpdateInquiry,
} from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

const STATUS = [
    { key: "new", label: "New", tone: "bg-emerald-100 text-emerald-800" },
    { key: "read", label: "Read", tone: "bg-slate-100 text-slate-700" },
    { key: "replied", label: "Replied", tone: "bg-blue-100 text-blue-800" },
    { key: "archived", label: "Archived", tone: "bg-[#E5E1DB] text-[#5C5855]" },
];
const STATUS_TONE = Object.fromEntries(STATUS.map((s) => [s.key, s.tone]));

const FORM_TYPES = [
    { key: "contact", label: "Contact" },
    { key: "start-project", label: "Start a project" },
    { key: "newsletter", label: "Newsletter" },
    { key: "other", label: "Other" },
];

const fmt = (d) =>
    new Date(d).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

export default function AdminInquiries() {
    const navigate = useNavigate();
    const [inquiries, setInquiries] = useState(null);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [formType, setFormType] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sort, setSort] = useState("desc");
    const [selected, setSelected] = useState(new Set());
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    const load = () => {
        adminListInquiries({
            q: q || undefined,
            status: status || undefined,
            form_type: formType || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            sort,
        })
            .then((res) => setInquiries(res.inquiries || []))
            .catch((err) =>
                setError(err?.response?.data?.detail || "Could not load inquiries")
            );
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, formType, dateFrom, dateTo, sort]);

    const totals = useMemo(() => {
        const t = { new: 0, read: 0, replied: 0, archived: 0 };
        (inquiries || []).forEach((i) => {
            const k = i.status || "new";
            t[k] = (t[k] || 0) + 1;
        });
        return t;
    }, [inquiries]);

    const toggleOne = (id) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (!inquiries) return;
        if (selected.size === inquiries.length) setSelected(new Set());
        else setSelected(new Set(inquiries.map((i) => i.id)));
    };

    const setInquiryStatus = async (id, newStatus) => {
        try {
            setBusy(true);
            const updated = await adminUpdateInquiry(id, { status: newStatus });
            setInquiries((rows) => rows.map((r) => (r.id === id ? updated : r)));
            if (active?.id === id) setActive(updated);
        } catch (err) {
            setError(err?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    const saveNotes = async (id, notes) => {
        try {
            setBusy(true);
            const updated = await adminUpdateInquiry(id, { notes });
            setInquiries((rows) => rows.map((r) => (r.id === id ? updated : r)));
            if (active?.id === id) setActive(updated);
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
        try {
            setBusy(true);
            await adminDeleteInquiry(id);
            setInquiries((rows) => rows.filter((r) => r.id !== id));
            if (active?.id === id) setActive(null);
            setSelected((s) => {
                const next = new Set(s);
                next.delete(id);
                return next;
            });
        } catch (err) {
            setError(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    const exportCsv = async () => {
        try {
            const ids = selected.size > 0 ? Array.from(selected) : null;
            const blob = await adminExportInquiries(ids);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            link.download = `asc-inquiries-${stamp}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err?.response?.data?.detail || "Export failed");
        }
    };

    const linkCls =
        "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";

    return (
        <main
            data-testid="page-admin-inquiries"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/inquiries" title="Inquiries — Admin" description="CRM for site inquiries" noindex />
            <AdminHeader title="Inquiries" />

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="inquiries-error">{error}</p>}

            {/* Status summary */}
            <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                {STATUS.map((s) => (
                    <div
                        key={s.key}
                        data-testid={`status-tile-${s.key}`}
                        className="bg-white border border-[#E5E1DB] px-5 py-4"
                    >
                        <p className="asc-overline text-[#8C7A6B]">{s.label}</p>
                        <p className="serif text-3xl mt-2 font-light">{totals[s.key] || 0}</p>
                    </div>
                ))}
            </section>

            {/* Filter bar */}
            <section className="mt-8 grid grid-cols-1 md:grid-cols-6 gap-3">
                <input
                    data-testid="filter-q"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && load()}
                    placeholder="Search name, email, company, message, notes…"
                    className="md:col-span-2 bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                />
                <select
                    data-testid="filter-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                >
                    <option value="">All statuses</option>
                    {STATUS.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                </select>
                <select
                    data-testid="filter-form-type"
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    className="bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                >
                    <option value="">All forms</option>
                    {FORM_TYPES.map((f) => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                </select>
                <input
                    data-testid="filter-date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                />
                <input
                    data-testid="filter-date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                />
            </section>

            <section className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4">
                    <button
                        onClick={load}
                        data-testid="apply-search"
                        className="text-[11px] uppercase tracking-[0.22em] bg-[#1A1A1A] text-[#F9F8F6] px-4 py-2"
                    >
                        Apply
                    </button>
                    <button
                        onClick={() => {
                            setQ("");
                            setStatus("");
                            setFormType("");
                            setDateFrom("");
                            setDateTo("");
                        }}
                        className={linkCls}
                    >
                        Clear filters
                    </button>
                    <select
                        data-testid="filter-sort"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                    >
                        <option value="desc">Newest first</option>
                        <option value="asc">Oldest first</option>
                    </select>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#8C7A6B]">
                        {selected.size > 0 ? `${selected.size} selected` : `${inquiries?.length || 0} total`}
                    </span>
                    <button
                        onClick={exportCsv}
                        data-testid="export-csv"
                        className="text-[11px] uppercase tracking-[0.22em] border border-[#1A1A1A] px-4 py-2 hover:bg-[#1A1A1A] hover:text-[#F9F8F6]"
                    >
                        Export CSV{selected.size > 0 ? ` (${selected.size})` : ""}
                    </button>
                </div>
            </section>

            {/* Table */}
            {inquiries == null ? (
                <p className="mt-10 asc-overline" data-testid="inquiries-loading">Loading…</p>
            ) : inquiries.length === 0 ? (
                <p className="mt-10 text-[#5C5855]" data-testid="inquiries-empty">No inquiries match the current filters.</p>
            ) : (
                <div className="mt-6 overflow-x-auto">
                    <table
                        data-testid="inquiries-table"
                        className="w-full text-left text-sm"
                    >
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] border-b border-[#E5E1DB]">
                                <th className="py-3 pr-2 w-10">
                                    <input
                                        type="checkbox"
                                        data-testid="select-all"
                                        checked={selected.size === inquiries.length && inquiries.length > 0}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className="py-3 pr-4">Received</th>
                                <th className="py-3 pr-4">Form</th>
                                <th className="py-3 pr-4">Name</th>
                                <th className="py-3 pr-4">Email</th>
                                <th className="py-3 pr-4">Status</th>
                                <th className="py-3 pr-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {inquiries.map((i) => (
                                <tr
                                    key={i.id}
                                    data-testid={`inquiry-row-${i.id}`}
                                    className="border-b border-[#E5E1DB] hover:bg-[#F2EFEB]"
                                >
                                    <td className="py-3 pr-2">
                                        <input
                                            type="checkbox"
                                            data-testid={`select-${i.id}`}
                                            checked={selected.has(i.id)}
                                            onChange={() => toggleOne(i.id)}
                                        />
                                    </td>
                                    <td className="py-3 pr-4 text-[#5C5855] whitespace-nowrap">{fmt(i.created_at)}</td>
                                    <td className="py-3 pr-4 text-[#5C5855] uppercase text-[10px] tracking-[0.18em]">
                                        {(i.form_type || "contact").replace("-", " ")}
                                    </td>
                                    <td className="py-3 pr-4 text-[#1A1A1A] font-medium">{i.name}</td>
                                    <td className="py-3 pr-4 text-[#5C5855]">
                                        <a href={`mailto:${i.email}`} className="asc-link">{i.email}</a>
                                    </td>
                                    <td className="py-3 pr-4">
                                        <span
                                            data-testid={`status-badge-${i.id}`}
                                            className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${STATUS_TONE[i.status || "new"]}`}
                                        >
                                            {i.status || "new"}
                                        </span>
                                    </td>
                                    <td className="py-3 pr-4 whitespace-nowrap">
                                        <button
                                            data-testid={`open-${i.id}`}
                                            onClick={() => {
                                                setActive(i);
                                                if (i.status === "new") setInquiryStatus(i.id, "read");
                                            }}
                                            className="text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] hover:underline mr-4"
                                        >
                                            Open
                                        </button>
                                        <button
                                            data-testid={`delete-${i.id}`}
                                            onClick={() => remove(i.id)}
                                            disabled={busy}
                                            className="text-[10px] uppercase tracking-[0.2em] text-red-700 hover:text-red-900 disabled:opacity-40"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Detail drawer */}
            {active && (
                <InquiryDetail
                    inquiry={active}
                    onClose={() => setActive(null)}
                    onStatus={(s) => setInquiryStatus(active.id, s)}
                    onNotes={(n) => saveNotes(active.id, n)}
                    onDelete={() => remove(active.id)}
                />
            )}
        </main>
    );
}

function AdminNav({ logout }) {
    const linkCls = "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";
    return (
        <nav className="flex items-center gap-6 flex-wrap">
            <Link to="/admin/dashboard" className={linkCls}>Dashboard</Link>
            <Link to="/admin/projects" className={linkCls}>Projects</Link>
            <Link to="/admin/articles" className={linkCls}>Studio Life</Link>
            <Link to="/admin/services-page" className={linkCls}>Services page</Link>
            <Link to="/admin/recognition" className={linkCls}>Recognition</Link>
            <Link to="/admin/clients" className={linkCls}>Clients</Link>
            <Link to="/admin/media" className={linkCls}>Media</Link>
            <Link to="/admin/settings" className={linkCls}>Settings</Link>
            <Link to="/admin/settings/email" className={linkCls}>Email</Link>
            <button onClick={logout} className={linkCls}>Sign out</button>
        </nav>
    );
}

function InquiryDetail({ inquiry, onClose, onStatus, onNotes, onDelete }) {
    const [notes, setNotes] = useState(inquiry.notes || "");
    const [saving, setSaving] = useState(false);
    useEffect(() => setNotes(inquiry.notes || ""), [inquiry.id, inquiry.notes]);

    return (
        <div
            data-testid="inquiry-detail-drawer"
            className="fixed inset-0 z-40 bg-black/40 flex justify-end"
            onClick={onClose}
        >
            <aside
                onClick={(e) => e.stopPropagation()}
                className="w-full sm:w-[520px] bg-[#F9F8F6] h-full overflow-y-auto"
            >
                <header className="flex items-center justify-between border-b border-[#E5E1DB] px-6 py-5">
                    <p className="asc-overline">Inquiry</p>
                    <button onClick={onClose} className="text-xs text-[#5C5855] hover:text-[#1A1A1A]">Close ✕</button>
                </header>
                <div className="px-6 py-6 space-y-6">
                    <div>
                        <h2 className="serif text-2xl tracking-tight">{inquiry.name}</h2>
                        <a href={`mailto:${inquiry.email}`} className="asc-link">{inquiry.email}</a>
                        {inquiry.phone && (
                            <p className="text-sm text-[#5C5855] mt-1">
                                <a href={`tel:${inquiry.phone}`} className="asc-link">{inquiry.phone}</a>
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {STATUS.map((s) => (
                            <button
                                key={s.key}
                                onClick={() => onStatus(s.key)}
                                data-testid={`set-status-${s.key}`}
                                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border ${
                                    (inquiry.status || "new") === s.key
                                        ? "bg-[#1A1A1A] text-[#F9F8F6] border-[#1A1A1A]"
                                        : "border-[#E5E1DB] text-[#5C5855] hover:border-[#1A1A1A]"
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                    <dl className="border-t border-[#E5E1DB] divide-y divide-[#E5E1DB]">
                        <Row k="Form" v={(inquiry.form_type || "contact").replace("-", " ")} />
                        <Row k="Company" v={inquiry.company || "—"} />
                        <Row k="Project type" v={inquiry.project_type || "—"} />
                        <Row k="Location" v={inquiry.location || "—"} />
                        <Row k="Budget" v={inquiry.budget || "—"} />
                        <Row k="Submitted from" v={inquiry.submitted_page || "—"} />
                        <Row k="IP" v={inquiry.ip || "—"} />
                        <Row k="Device" v={inquiry.user_agent || "—"} />
                        <Row k="Email sent" v={inquiry.email_sent ? "Yes" : "No"} />
                        <Row k="Received" v={fmt(inquiry.created_at)} />
                    </dl>
                    <div>
                        <p className="asc-overline mb-2">Message</p>
                        <p
                            data-testid="inquiry-message"
                            className="text-[#1A1A1A] leading-relaxed whitespace-pre-line bg-white p-4 border border-[#E5E1DB]"
                        >
                            {inquiry.message}
                        </p>
                    </div>
                    <div>
                        <p className="asc-overline mb-2">Internal notes</p>
                        <textarea
                            data-testid="inquiry-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            className="w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-3 py-2 text-sm"
                        />
                        <div className="flex items-center justify-between mt-3">
                            <button
                                onClick={async () => {
                                    setSaving(true);
                                    await onNotes(notes);
                                    setSaving(false);
                                }}
                                data-testid="save-notes"
                                disabled={saving}
                                className="px-4 py-2 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                            >
                                {saving ? "Saving…" : "Save notes"}
                            </button>
                            <button
                                onClick={onDelete}
                                data-testid="detail-delete"
                                className="text-[11px] uppercase tracking-[0.22em] text-red-700 hover:text-red-900"
                            >
                                Delete inquiry
                            </button>
                        </div>
                    </div>
                </div>
            </aside>
        </div>
    );
}

const Row = ({ k, v }) => (
    <div className="grid grid-cols-3 py-2 text-sm">
        <dt className="col-span-1 asc-overline text-[#8C7A6B]">{k}</dt>
        <dd className="col-span-2 text-[#1A1A1A]">{v}</dd>
    </div>
);
